import { useState } from 'react';
import { toast } from 'sonner';
import { Copy, Check, Type, AlignLeft, Lock } from 'lucide-react';
import { api } from '../lib/api';

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text; document.body.appendChild(ta); ta.select();
    document.execCommand('copy'); ta.remove();
  }
  toast.success(`${label} copié ✓`);
};

export function CopyButton({ text, label, testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      data-testid={testid}
      disabled={!text}
      onClick={(e) => { e.stopPropagation(); copyText(text, label); setDone(true); setTimeout(() => setDone(false), 1600); }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 9, flexShrink: 0,
        background: done ? 'rgba(52,211,153,0.18)' : 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)',
        border: 'none', color: done ? '#34D399' : '#0B1330', cursor: text ? 'pointer' : 'not-allowed', opacity: text ? 1 : 0.4,
        fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
        transition: 'background-color 0.14s ease',
      }}
    >{done ? <Check size={12} strokeWidth={3} /> : <Copy size={12} strokeWidth={2.6} />}{done ? 'Copié' : 'Copier'}</button>
  );
}

// Bloc lecture « à copier-coller » (posteur / monteur)
export function CopyField({ icon: Icon, label, text, testid, multiline, emptyLabel = 'Pas encore renseigné' }) {
  return (
    <div data-testid={testid} style={{ padding: '10px 12px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid var(--bd, rgba(62,123,250,0.18))' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Icon size={12} color="#5BBEFC" />
        <span style={{ flex: 1, fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5, color: 'rgba(200,215,240,0.6)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</span>
        <CopyButton text={text} label={label} testid={`${testid}-copy`} />
      </div>
      <div data-testid={`${testid}-value`} style={{ fontFamily: 'Lexend', fontSize: multiline ? 12.5 : 13.5, fontWeight: multiline ? 400 : 600, color: text ? '#F1F4F9' : 'rgba(200,215,240,0.4)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5, userSelect: 'text' }}>
        {text || emptyLabel}
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '8px 11px', background: 'var(--sf)', border: '1px solid var(--bd)', borderRadius: 8,
  color: 'var(--t1)', fontFamily: 'Lexend', fontSize: 13,
};

// Fiche vidéo projet : Titre (monteur + posteur) & Description (posteur, visible dès « À poster »)
export function PublicationBlock({ task, canEdit, onSaved }) {
  const [title, setTitle] = useState(task.publication_title || '');
  const [desc, setDesc] = useState(task.publication_description || '');
  const showDesc = canEdit || ['to_post', 'done'].includes(task.stage);
  const save = async (field, value) => {
    try {
      await api.patch(`/tasks/${task.id}`, { [field]: value });
      toast.success('Enregistré');
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  return (
    <div data-testid="publication-block" style={{ padding: '14px 16px', borderRadius: 14, background: 'rgba(91,190,252,0.05)', border: '1.5px solid rgba(91,190,252,0.35)', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="asa-eyebrow" style={{ color: '#5BBEFC' }}>Titre & description de la vidéo</div>
      {canEdit ? (
        <>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span className="asa-eyebrow" style={{ flex: 1 }}>Titre de la vidéo</span>
              <CopyButton text={title} label="Titre" testid="publication-title-copy" />
            </div>
            <input data-testid="publication-title-input" value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => title !== (task.publication_title || '') && save('publication_title', title)} placeholder="Le titre exact que le monteur met sur la vidéo / le doc" style={inputStyle} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span className="asa-eyebrow" style={{ flex: 1 }}>Description du post</span>
              <CopyButton text={desc} label="Description" testid="publication-desc-copy" />
            </div>
            <textarea data-testid="publication-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} onBlur={() => desc !== (task.publication_description || '') && save('publication_description', desc)} rows={4} placeholder="Légende à coller au moment de poster (hashtags, liens…)" style={{ ...inputStyle, fontSize: 12.5, resize: 'vertical' }} />
            <div style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
              <Lock size={10} /> Le monteur ne voit pas la description. Le posteur la voit dès que la vidéo passe « À poster ».
            </div>
          </div>
        </>
      ) : (
        <>
          <CopyField icon={Type} label="Titre de la vidéo" text={task.publication_title || task.title} testid="publication-title" />
          {showDesc && <CopyField icon={AlignLeft} label="Description du post" text={task.publication_description} testid="publication-desc" multiline />}
        </>
      )}
    </div>
  );
}
